import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from '../utils/axios';
import {
  accountMarketplace,
  normalizeFulfillmentMp,
  pickFulfillmentAccount,
} from '../utils/fulfillmentApi.mjs';

const LAST_CABINET_KEY = 'fulfillment_tsd_last_cabinet';

function readStoredCabinetId() {
  try {
    const raw = localStorage.getItem(LAST_CABINET_KEY);
    return raw && String(raw).trim() ? String(raw).trim() : '';
  } catch {
    return '';
  }
}

function writeStoredCabinetId(id) {
  try {
    if (id == null || String(id).trim() === '') {
      localStorage.removeItem(LAST_CABINET_KEY);
      return;
    }
    localStorage.setItem(LAST_CABINET_KEY, String(id));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Shared TSD/desktop cabinet picker: loads GET /fulfillment/unified/accounts
 * and syncs ?mp=&account_id= in the URL. Last cabinet remembered in localStorage
 * when deep-link is absent.
 */
export default function useFulfillmentCabinet({ enabled = true } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(Boolean(enabled));
  const [accountsError, setAccountsError] = useState('');
  const [accountId, setAccountIdState] = useState('');

  const queryMp = searchParams.get('mp') || searchParams.get('marketplace') || '';
  const queryAccountId = searchParams.get('account_id') || searchParams.get('accountId') || '';

  useEffect(() => {
    if (!enabled) {
      setAccountsLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setAccountsLoading(true);
      setAccountsError('');
      try {
        const { data } = await axios.get('/fulfillment/unified/accounts', { silent: true });
        const list = data?.items || data?.accounts || data || [];
        if (cancelled) return;
        const normalized = (Array.isArray(list) ? list : []).map((row) => ({
          ...row,
          id: row.id,
          marketplace: accountMarketplace(row),
        }));
        setAccounts(normalized);
        const storedId = readStoredCabinetId();
        const picked = pickFulfillmentAccount(normalized, {
          account_id: queryAccountId || storedId,
          mp: queryMp,
        });
        setAccountIdState((prev) => {
          if (prev && normalized.some((item) => String(item.id) === String(prev))) return prev;
          return picked;
        });
      } catch (error) {
        if (!cancelled) {
          setAccountsError(error?.response?.data?.message || error.message || 'Не удалось загрузить кабинеты');
          setAccounts([]);
        }
      } finally {
        if (!cancelled) setAccountsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Intentionally only on mount / enabled — deep-link applied once from initial query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const selectedAccount = useMemo(
    () => accounts.find((item) => String(item.id) === String(accountId)) || null,
    [accounts, accountId],
  );

  const marketplace = accountMarketplace(selectedAccount) || normalizeFulfillmentMp(queryMp) || 'wb';

  const setAccountId = useCallback((nextId) => {
    const id = String(nextId || '');
    setAccountIdState(id);
    writeStoredCabinetId(id);
  }, []);

  // Sync deep-link query when cabinet changes.
  useEffect(() => {
    if (!enabled || !accountId) return;
    writeStoredCabinetId(accountId);
    const curId = searchParams.get('account_id') || '';
    const curMp = normalizeFulfillmentMp(searchParams.get('mp') || '');
    const nextMp = normalizeFulfillmentMp(marketplace);
    if (String(curId) === String(accountId) && curMp === nextMp) return;
    const next = new URLSearchParams(searchParams);
    next.set('account_id', String(accountId));
    if (nextMp) next.set('mp', nextMp);
    else next.delete('mp');
    setSearchParams(next, { replace: true });
  }, [accountId, marketplace, enabled, searchParams, setSearchParams]);

  return {
    accounts,
    accountsLoading,
    accountsError,
    accountId,
    setAccountId,
    selectedAccount,
    marketplace,
    isOzon: marketplace === 'ozon',
    isWb: marketplace === 'wb',
    isYandex: marketplace === 'yandex',
    isLocalWarehouse: marketplace === 'ozon' || marketplace === 'yandex',
  };
}
